import { useImpersonationStatus } from "./use-impersonation-status";
import { useCountdown, formatCountdown } from "./use-countdown";
import { getCSRFToken } from "@ddmarc/shared/auth";

/**
 * Persistent impersonation warning banner.
 *
 * Renders at the very top of the page (fixed, full-width) whenever an
 * admin support agent is impersonating the current user. Intentionally
 * uses amber/warning colors — NOT brand colors — so it reads as an alert
 * in any white-label context.
 *
 * Cannot be dismissed. High z-index (2147483646) to survive extension CSS.
 * Pushes body content down via --impersonation-banner-height CSS variable.
 */
export function ImpersonationBanner() {
  const status = useImpersonationStatus();

  if (!status) return null;

  return (
    <BannerInner
      agentEmail={status.agent_email}
      expiresAt={status.expires_at}
      writeEnabled={status.write_enabled}
      sessionId={status.session_id}
    />
  );
}

interface BannerInnerProps {
  agentEmail: string;
  expiresAt: string;
  writeEnabled: boolean;
  sessionId: string;
}

function BannerInner({ agentEmail, expiresAt, writeEnabled }: BannerInnerProps) {
  const remaining = useCountdown(expiresAt);
  const formatted = formatCountdown(remaining);
  const expired = remaining <= 0;

  // Reload on natural expiry — clears in-memory state exactly like End Session
  if (expired) {
    window.location.reload();
    return null;
  }

  async function handleEndSession() {
    const csrf = getCSRFToken() ?? "";
    try {
      await fetch("/api/v1/auth/impersonation/end", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
        },
      });
    } catch {
      // Best-effort — reload regardless to clear client state
    }
    window.location.reload();
  }

  return (
    <>
      {/* Sticky banner — fixed to viewport top, max z-index */}
      <div
        id="impersonation-banner"
        role="alert"
        aria-label="Support impersonation session active"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2147483646,
          backgroundColor: "#d97706", // amber-600
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.75rem",
          padding: "0.5rem 1rem",
          fontSize: "0.875rem",
          fontFamily: "system-ui, sans-serif",
          flexWrap: "wrap",
        }}
      >
        {/* Warning icon */}
        <span aria-hidden="true" style={{ fontSize: "1rem", lineHeight: 1 }}>
          ⚠
        </span>

        {/* Agent info */}
        <span>
          <strong>Support session active</strong>
          {" — "}
          Agent: <strong>{agentEmail}</strong>
        </span>

        {/* Write-enabled badge */}
        {writeEnabled && (
          <span
            style={{
              backgroundColor: "#92400e",
              borderRadius: "0.25rem",
              padding: "0.125rem 0.5rem",
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.025em",
            }}
          >
            Write actions enabled
          </span>
        )}

        {/* Countdown — aria-live so screen readers announce updates */}
        <span role="status" aria-live="polite" aria-atomic="true">
          Expires in{" "}
          <span
            style={{ fontFamily: "monospace", fontVariantNumeric: "tabular-nums" }}
          >
            {formatted}
          </span>
        </span>

        {/* End session button */}
        <button
          onClick={() => void handleEndSession()}
          style={{
            backgroundColor: "#ffffff",
            color: "#92400e",
            border: "none",
            borderRadius: "0.25rem",
            padding: "0.25rem 0.75rem",
            fontSize: "0.8125rem",
            fontWeight: 600,
            cursor: "pointer",
            marginLeft: "0.25rem",
          }}
          onMouseOver={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#fef3c7";
          }}
          onMouseOut={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#ffffff";
          }}
        >
          End Session
        </button>
      </div>

      {/* Spacer — pushes app content below the fixed banner */}
      <div
        aria-hidden="true"
        style={{ height: "2.5rem", flexShrink: 0 }}
      />
    </>
  );
}
